import React, { useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';

const IDLE_TIMEOUT = 15 * 60 * 1000; // 15 minutes
const REFRESH_THROTTLE = 60 * 1000; // Refresh backend at most once per 60 seconds

/**
 * Idle watchdog for a signed-in user: keeps the backend session warm while
 * they are active and logs them out after IDLE_TIMEOUT without input.
 *
 * Only armed while authenticated. Mounted unconditionally it also ran on the
 * login page, where it hit /refresh-token on every minute of mouse movement
 * and, after fifteen idle minutes, fired a logout that the 401 interceptor
 * would refresh-and-retry -- ending a live session that belonged to a portal
 * tab, for a visitor who had never signed in on this page.
 */
const IdleLogout: React.FC = () => {
  const { isAuthenticated, logout, refreshToken } = useAuth();
  const lastRefreshRef = useRef<number>(0);

  useEffect(() => {
    if (!isAuthenticated) return;

    let idleTimer: ReturnType<typeof setTimeout>;

    const autoLogout = async () => {
      await logout();
    };

    const resetIdleTimer = () => {
      clearTimeout(idleTimer);
      idleTimer = setTimeout(autoLogout, IDLE_TIMEOUT);

      // Throttled: refresh backend session at most once per 60 seconds
      const now = Date.now();
      if (now - lastRefreshRef.current > REFRESH_THROTTLE) {
        lastRefreshRef.current = now;
        refreshToken();
      }
    };

    const activityEvents = ['mousemove', 'keydown', 'scroll', 'click', 'touchstart'];
    activityEvents.forEach(event => window.addEventListener(event, resetIdleTimer));

    resetIdleTimer(); // start timer initially

    return () => {
      clearTimeout(idleTimer);
      activityEvents.forEach(event => window.removeEventListener(event, resetIdleTimer));
    };
  }, [isAuthenticated, logout, refreshToken]);

  return null;
};

export default IdleLogout;
